import type { FileLibraryDocument, FileLibraryGroup } from "@/api/file_library";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FileCenterGridLabel } from "@/components/file_viewer/fileCenterLabels";
import {
  FileCenterItemTooltip,
  FileCenterTooltipProvider,
} from "@/components/file_viewer/fileCenterTooltips";
import { cn } from "@/lib/utils";
import { buildChildrenByParentId } from "@/lib/documentGroupTree";
import { FileCenterBreadcrumbs, findGroupIdForDocument } from "@/components/file_viewer/fileCenterBreadcrumbs";
import { DocumentCommentsPanel } from "@/components/file_viewer/DocumentCommentsPanel";
import { FileCenterDocumentChatPane } from "@/components/file_viewer/FileCenterDocumentChatPane";
import {
  fileLibraryDocumentChatEnabled,
  fileLibraryDocumentChatUnavailableMessage,
} from "@/api/file_library_document_chat";
import {
  ArrowLeft,
  Download,
  ExternalLink,
  FileArchive,
  FileCode,
  FileText,
  Folder,
  Mail,
  MessageCircle,
  MessageSquare,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";

/**
 * PDF stays visible (min ~240px); side panel uses a fixed width so chat and comments
 * match when toggling and when the library sidebar collapses for focus mode.
 */
export const FILE_CENTER_PREVIEW_SIDE_PANEL_WIDTH_CLASS =
  "[grid-template-columns:minmax(240px,1fr)_22rem] lg:[grid-template-columns:minmax(280px,1fr)_24rem]";

const PREVIEW_WITH_SIDE_PANEL_GRID_CLASS = cn(
  "grid h-full min-h-0 w-full min-w-0 overflow-hidden",
  FILE_CENTER_PREVIEW_SIDE_PANEL_WIDTH_CLASS,
);

type PreviewSidePanelMode = "chat" | "comments" | null;

const PREVIEW_COLUMN_CLASS = "min-h-0 min-w-0 overflow-hidden bg-muted/20";

const SIDE_PANEL_COLUMN_CLASS =
  "flex h-full min-h-0 min-w-0 flex-col overflow-hidden border-l border-border/80 bg-background shadow-[inset_6px_0_16px_-12px_rgba(0,0,0,0.12)]";

/** Center breadcrumb row. */
const FILE_CENTER_NAV_ROW_BASE = "flex h-12 shrink-0 items-center gap-2 px-4 text-sm";

export const FILE_CENTER_NAV_ROW_CLASS = `${FILE_CENTER_NAV_ROW_BASE} border-b`;

type FileCenterFolderGridProps = {
  groups: FileLibraryGroup[];
  documents: FileLibraryDocument[];
  rootDocuments?: FileLibraryDocument[];
  /** Root breadcrumb when browsing a folder or previewing a document. Hidden at the library root. */
  rootLabel: string;
  browseFolderId: number | null;
  onBrowseFolder: (folderId: number | null) => void;
  onPickDocument: (doc: FileLibraryDocument) => void;
  selectedDocId: number | null;
  previewDocument?: FileLibraryDocument | null;
  onClosePreview?: () => void;
  emptyMessage?: ReactNode;
  municipalityId?: number | null;
  /** Fires when either chat or comments side panel opens or closes (drives focus layout). */
  onSidePanelOpenChange?: (open: boolean) => void;
  className?: string;
};

type GridItemCardProps = {
  label: string;
  subtitle?: string | null;
  icon: LucideIcon;
  selected?: boolean;
  onClick: () => void;
};

type DocumentPreviewLayoutProps = {
  document: FileLibraryDocument;
  sidePanelMode: PreviewSidePanelMode;
  municipalityId: number | null;
  onCloseSidePanel: () => void;
};

function documentLabel(document: FileLibraryDocument) {
  return document.display_filename || document.name;
}

function previewUrlFor(document: FileLibraryDocument) {
  if (document.previewable !== true) return null;

  return document.preview_url ?? document.file_path;
}

function downloadUrlFor(document: FileLibraryDocument) {
  if (document.downloadable !== true) return null;

  return document.download_url;
}

function fileKindLabel(document: FileLibraryDocument) {
  return document.file_kind_label || "File";
}

function iconForFile(document: FileLibraryDocument): LucideIcon {
  switch (document.file_icon) {
    case "mail":
      return Mail;
    case "code":
      return FileCode;
    case "archive":
      return FileArchive;
    default:
      return FileText;
  }
}

function NonPreviewableDocumentPanel({ document }: { document: FileLibraryDocument }) {
  const downloadUrl = downloadUrlFor(document);

  return (
    <div className="flex flex-1 items-center justify-center p-8">
      <div className="max-w-md rounded-2xl border bg-background p-6 text-center shadow-sm">
        <FileText className="mx-auto h-12 w-12 text-muted-foreground" aria-hidden />
        <h2 className="mt-4 truncate text-lg font-semibold">{documentLabel(document)}</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          This file is available to download, but it does not have an in-browser preview.
        </p>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-xs text-muted-foreground">
          <span>{fileKindLabel(document)}</span>
        </div>
        {downloadUrl && (
          <Button asChild className="mt-6">
            <a href={downloadUrl} className="inline-flex items-center gap-2">
              <Download className="h-4 w-4 shrink-0" aria-hidden />
              <span>Download</span>
            </a>
          </Button>
        )}
      </div>
    </div>
  );
}

function DocumentPreviewLayout({
  document,
  sidePanelMode,
  municipalityId,
  onCloseSidePanel,
}: DocumentPreviewLayoutProps) {
  const previewUrl = previewUrlFor(document);
  const preview = previewUrl ? (
    <iframe
      title={document.name}
      src={previewUrl}
      className="h-full w-full flex-1 border-0 bg-background"
    />
  ) : (
    <NonPreviewableDocumentPanel document={document} />
  );

  if (!sidePanelMode || municipalityId == null) {
    return (
      <div className="flex h-full min-h-0 min-w-0 w-full flex-1 flex-col overflow-hidden">{preview}</div>
    );
  }

  return (
    <div className={PREVIEW_WITH_SIDE_PANEL_GRID_CLASS} data-testid="file-center-preview-with-side-panel">
      <div className={cn(PREVIEW_COLUMN_CLASS, "flex flex-col")}>{preview}</div>
      <div className={SIDE_PANEL_COLUMN_CLASS}>
        {sidePanelMode === "chat" ? (
          <FileCenterDocumentChatPane
            municipalityId={municipalityId}
            document={document}
            onClose={onCloseSidePanel}
          />
        ) : (
          <DocumentCommentsPanel
            municipalityId={municipalityId}
            documentId={document.id}
            className="h-full min-h-0 w-full"
          />
        )}
      </div>
    </div>
  );
}

function GridItemCard({ label, subtitle, icon: Icon, selected, onClick }: GridItemCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group grid w-full min-w-0 grid-cols-[auto_minmax(0,1fr)] items-center gap-3 rounded-2xl bg-[#ececee] dark:bg-muted/60 px-4 py-3 text-left hover:bg-[#e2e3e6] dark:hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        selected && "ring-2 ring-ring",
      )}
    >
      <Icon
        className="h-6 w-6 shrink-0 text-muted-foreground group-hover:text-foreground transition-colors"
        aria-hidden
      />
      <div className="min-w-0 flex-1 overflow-hidden">
        <FileCenterGridLabel label={label} />
        {subtitle && (
          <span className="block truncate text-xs text-muted-foreground" title={subtitle}>
            {subtitle}
          </span>
        )}
      </div>
    </button>
  );
}

export function FileCenterFolderGrid({
  groups,
  documents,
  rootDocuments = [],
  rootLabel,
  browseFolderId,
  onBrowseFolder,
  onPickDocument,
  selectedDocId,
  previewDocument = null,
  onClosePreview,
  emptyMessage,
  municipalityId = null,
  onSidePanelOpenChange,
  className,
}: FileCenterFolderGridProps) {
  const [sidePanelMode, setSidePanelMode] = useState<PreviewSidePanelMode>(null);

  const chatAvailable =
    previewDocument != null &&
    municipalityId != null &&
    fileLibraryDocumentChatEnabled(previewDocument);

  const chatActive = sidePanelMode === "chat" && Boolean(previewDocument) && chatAvailable;
  const commentsActive = sidePanelMode === "comments" && Boolean(previewDocument) && municipalityId != null;
  const sidePanelActive = chatActive || commentsActive;
  const chatUnavailableMessage = previewDocument ? fileLibraryDocumentChatUnavailableMessage(previewDocument) : null;

  useEffect(() => {
    setSidePanelMode(null);
  }, [previewDocument?.id]);

  useEffect(() => {
    onSidePanelOpenChange?.(sidePanelActive);
  }, [sidePanelActive, onSidePanelOpenChange]);

  const groupsById = useMemo(() => new Map(groups.map((g) => [g.id, g])), [groups]);
  const childrenByParent = useMemo(() => buildChildrenByParentId(groups), [groups]);
  const docById = useMemo(() => new Map(documents.map((d) => [d.id, d])), [documents]);

  const childFolders = useMemo(() => childrenByParent.get(browseFolderId) ?? [], [childrenByParent, browseFolderId]);

  const folderDocuments = useMemo(() => {
    if (browseFolderId == null) return rootDocuments;
    const group = groupsById.get(browseFolderId);
    if (!group) return [];
    return group.document_ids
      .map((id) => docById.get(id))
      .filter((d): d is FileLibraryDocument => Boolean(d));
  }, [browseFolderId, groupsById, docById, rootDocuments]);

  const parentFolderId =
    browseFolderId == null ? null : (groupsById.get(browseFolderId)?.parent_id ?? null);

  /** Preview breadcrumbs follow the file's folder, not center-grid browse state. */
  const previewBreadcrumbFolderId = useMemo(() => {
    if (!previewDocument) return browseFolderId;
    // group_id rides on lazily-loaded / searched documents (ENG-1422); fall back to a tree scan only
    // for any legacy document that predates it.
    if (previewDocument.group_id !== undefined) return previewDocument.group_id;
    return findGroupIdForDocument(previewDocument.id, groups);
  }, [previewDocument, groups, browseFolderId]);

  const handleBack = () => {
    if (previewDocument) {
      setSidePanelMode(null);
      onClosePreview?.();
      return;
    }

    onBrowseFolder(parentFolderId);
  };

  const previewBody =
    previewDocument ? (
      <DocumentPreviewLayout
        document={previewDocument}
        sidePanelMode={sidePanelActive ? sidePanelMode : null}
        municipalityId={municipalityId}
        onCloseSidePanel={() => setSidePanelMode(null)}
      />
    ) : null;

  const previewUrl = previewDocument ? previewUrlFor(previewDocument) : null;
  const downloadUrl = previewDocument ? downloadUrlFor(previewDocument) : null;

  const chatButton =
    municipalityId != null ? (
      <Button
        type="button"
        variant={chatActive ? "secondary" : "outline"}
        size="sm"
        className="shrink-0"
        disabled={!chatAvailable}
        aria-disabled={!chatAvailable || undefined}
        onClick={() => setSidePanelMode((mode) => (mode === "chat" ? null : "chat"))}
        aria-pressed={chatActive}
      >
        <MessageCircle className="h-4 w-4 shrink-0" aria-hidden />
        <span className="ml-2">Chat</span>
      </Button>
    ) : null;

  const previewActions = previewDocument ? (
    <div className="flex shrink-0 items-center gap-1.5">
      {municipalityId != null && (
        <Button
          type="button"
          variant={commentsActive ? "secondary" : "outline"}
          size="sm"
          className="shrink-0"
          onClick={() =>
            setSidePanelMode((mode) => (mode === "comments" ? null : "comments"))
          }
          aria-pressed={commentsActive}
        >
          <MessageSquare className="h-4 w-4 shrink-0" aria-hidden />
          <span className="ml-2">Comments</span>
        </Button>
      )}
      {chatButton &&
        (chatAvailable || !chatUnavailableMessage ? (
          chatButton
        ) : (
          <FileCenterTooltipProvider>
            <FileCenterItemTooltip label={chatUnavailableMessage}>
              <span className="inline-flex shrink-0">
                {chatButton}
              </span>
            </FileCenterItemTooltip>
          </FileCenterTooltipProvider>
        ))}
      {downloadUrl && (
        <Button variant="outline" size="sm" className="shrink-0" asChild>
          <a href={downloadUrl} className="inline-flex items-center gap-2">
            <Download className="h-4 w-4 shrink-0" aria-hidden />
            <span>Download</span>
          </a>
        </Button>
      )}
      {previewUrl && (
        <Button variant="outline" size="sm" className="shrink-0" asChild>
          <a
            href={previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2"
          >
            <ExternalLink className="h-4 w-4 shrink-0" aria-hidden />
            <span>Open</span>
          </a>
        </Button>
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="shrink-0"
        onClick={handleBack}
      >
        <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
        <span className="ml-1">Back</span>
      </Button>
    </div>
  ) : null;

  const showLocationNav = browseFolderId != null || previewDocument != null;

  return (
    <div className={cn("flex min-h-0 flex-1 flex-col overflow-hidden bg-background border-r", className)}>
      {showLocationNav ? (
        <nav
          aria-label="Folder location"
          className={cn(FILE_CENTER_NAV_ROW_CLASS, "min-w-0 w-full items-center gap-2 overflow-hidden")}
        >
          {previewDocument ? (
            <>
              <div className="min-w-0 flex-1 overflow-x-auto overflow-y-hidden">
                <FileCenterBreadcrumbs
                  rootLabel={rootLabel}
                  groups={groups}
                  browseFolderId={previewBreadcrumbFolderId}
                  onBrowseFolder={onBrowseFolder}
                  currentDocument={previewDocument}
                />
              </div>
              {previewActions}
            </>
          ) : (
            <>
              <div className="min-w-0 flex-1 overflow-x-auto overflow-y-hidden">
                <FileCenterBreadcrumbs
                  rootLabel={rootLabel}
                  groups={groups}
                  browseFolderId={browseFolderId}
                  onBrowseFolder={onBrowseFolder}
                />
              </div>
              {browseFolderId != null && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="shrink-0"
                  onClick={handleBack}
                >
                  <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
                  <span className="ml-1">Back</span>
                </Button>
              )}
            </>
          )}
        </nav>
      ) : null}

      {previewDocument ? (
        <div
          className={cn(
            "flex min-h-0 min-w-0 w-full flex-1 flex-col overflow-hidden",
            sidePanelActive ? "bg-background" : "bg-muted/20",
          )}
          data-testid="file-center-folder-grid"
        >
          {previewBody}
        </div>
      ) : (
        <ScrollArea
          className="flex-1 min-h-0"
          viewportClassName="[&>div]:!block [&>div]:!w-full [&>div]:max-w-full"
          scrollbarSize="lg"
          data-testid="file-center-folder-grid"
        >
          <div className="box-border w-full max-w-full min-w-0 p-4">
            {childFolders.length === 0 && folderDocuments.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-12">
                {emptyMessage ?? "This folder is empty."}
              </div>
            )}

            {(childFolders.length > 0 || folderDocuments.length > 0) && (
              <FileCenterTooltipProvider>
                <div className="flex flex-col gap-2">
                  {childFolders.map((folder) => (
                    <GridItemCard
                      key={folder.id}
                      label={folder.name}
                      icon={Folder}
                      onClick={() => onBrowseFolder(folder.id)}
                    />
                  ))}
                  {folderDocuments.map((doc) => (
                    <GridItemCard
                      key={doc.id}
                      label={documentLabel(doc)}
                      subtitle={doc.file_kind_label}
                      icon={iconForFile(doc)}
                      selected={selectedDocId === doc.id}
                      onClick={() => onPickDocument(doc)}
                    />
                  ))}
                </div>
              </FileCenterTooltipProvider>
            )}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}
